/* Skybox (GoldSrc env/<name>{rt,lf,up,dn,ft,bk}.tga): TGA/Bild lesen, in die sechs Würfelseiten umrechnen. */
import I18n from '../i18n.ts';
/** Bild als RGBA, Zeile 0 oben */
export interface Image { w: number; h: number; rgba: Uint8ClampedArray }
type V3 = [number, number, number];

const Skybox = (function () {
  'use strict';
  const _ = I18n.t;
  const SIDES = ['rt', 'lf', 'up', 'dn', 'ft', 'bk'];
  /** TGA (Typ 2/3/10, 8/24/32 Bit) -> {w,h,rgba} (oben beginnend) */
  function parseTga(buf: ArrayBuffer): Image {
    const u = new Uint8Array(buf);
    if (u.length < 18) throw new Error(_('sky.e_short'));
    const idLen = u[0], cmType = u[1], type = u[2], w = u[12] | (u[13] << 8), h = u[14] | (u[15] << 8), bpp = u[16], desc = u[17];
    if (cmType !== 0 || (type !== 2 && type !== 10 && type !== 3 && type !== 11)) throw new Error(_('sky.e_type', { type: type }));
    const Bpp = bpp >> 3; if (![1, 3, 4].includes(Bpp)) throw new Error(_('sky.e_bpp', { bpp: bpp }));
    const raw = new Uint8Array(w * h * Bpp); let p = 18 + idLen, o = 0;
    if (type === 2 || type === 3) { raw.set(u.subarray(p, p + raw.length)); }
    else {
      while (o < raw.length && p < u.length) {
        const c = u[p++], n = (c & 127) + 1;
        if (c & 128) { for (let k = 0; k < n && o < raw.length; k++) for (let b = 0; b < Bpp; b++) raw[o++] = u[p + b]; p += Bpp; }
        else { for (let k = 0; k < n * Bpp && o < raw.length; k++) raw[o++] = u[p++]; }
      }
    }
    const rgba = new Uint8ClampedArray(w * h * 4), top = !!(desc & 32), right = !!(desc & 16);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const sy = top ? y : h - 1 - y, sx = right ? w - 1 - x : x, s = (sy * w + sx) * Bpp, d = (y * w + x) * 4;
      if (Bpp === 1) { rgba[d] = rgba[d + 1] = rgba[d + 2] = raw[s]; rgba[d + 3] = 255; }
      else { rgba[d] = raw[s + 2]; rgba[d + 1] = raw[s + 1]; rgba[d + 2] = raw[s]; rgba[d + 3] = Bpp === 4 ? raw[s + 3] : 255; }
    }
    return { w: w, h: h, rgba: rgba };
  }
  /** "desertft.tga" -> {name:'desert', side:'ft'} oder null */
  function nameOf(fn: unknown): { name: string; side: string } | null {
    const m = /^(.*?)(rt|lf|up|dn|ft|bk)\.(tga|png|jpg|jpeg|bmp)$/i.exec(String(fn).split(/[\\/]/).pop() as string);
    return m ? { name: m[1].toLowerCase(), side: m[2].toLowerCase() } : null;
  }
  // Blickrichtung d, rechts R, oben T je Seite in Spielkoordinaten (z oben, Blick von innen)
  const SRC: Record<string, { R: V3; T: V3 }> = {
    ft: { R: [0, -1, 0], T: [0, 0, 1] }, bk: { R: [0, 1, 0], T: [0, 0, 1] }, lf: { R: [1, 0, 0], T: [0, 0, 1] }, rt: { R: [-1, 0, 0], T: [0, 0, 1] },
    up: { R: [0, -1, 0], T: [-1, 0, 0] }, dn: { R: [0, -1, 0], T: [1, 0, 0] },
  };
  // BoxGeometry-Reihenfolge +x,-x,+y,-y,+z,-z (three.js): Quelle + Achsen der Zielbild-Pixel (R = u-Richtung, T = v-Richtung) in Spielkoordinaten.
  // Das Bild wird innen auf einem BackSide-Würfel gesehen, daher ist R das Gegenteil der Blickrechten.
  const TARGET: { side: string; R: V3; T: V3 }[] = [
    { side: 'ft', R: [0, 1, 0], T: [0, 0, 1] }, { side: 'bk', R: [0, -1, 0], T: [0, 0, 1] },
    { side: 'up', R: [1, 0, 0], T: [0, 1, 0] }, { side: 'dn', R: [1, 0, 0], T: [0, -1, 0] },
    { side: 'rt', R: [1, 0, 0], T: [0, 0, 1] }, { side: 'lf', R: [-1, 0, 0], T: [0, 0, 1] },
  ];
  function rot90(im: Image, k: number): Image {
    k = ((k % 4) + 4) % 4; if (!k) return im;
    let cur = im;
    for (let r = 0; r < k; r++) {
      const n = cur.w, out = new Uint8ClampedArray(n * n * 4);
      for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) { const s = (y * n + x) * 4, d = (x * n + (n - 1 - y)) * 4; out[d] = cur.rgba[s]; out[d + 1] = cur.rgba[s + 1]; out[d + 2] = cur.rgba[s + 2]; out[d + 3] = cur.rgba[s + 3]; }
      cur = { w: n, h: n, rgba: out };
    }
    return cur;
  }
  const dot = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  /** imgs: {side: {w,h,rgba}} -> sechs RGBA-Bilder (Größe n) in three.js-Reihenfolge, mit Drehung/Spiegelung der Quellen */
  function toCube(imgs: Record<string, Image>, rot?: Record<string, number>): Image[] {
    return TARGET.map((t) => {
      const im = rot90(imgs[t.side], rot && rot[t.side] || 0), s = SRC[t.side], n = im.w, out = new Uint8ClampedArray(n * n * 4);
      for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
        const a = (x + 0.5) / n * 2 - 1, b = 1 - (y + 0.5) / n * 2; // Ziel: rechts a, oben b
        const v: V3 = [t.R[0] * a + t.T[0] * b, t.R[1] * a + t.T[1] * b, t.R[2] * a + t.T[2] * b];
        const sa = dot(v, s.R), sb = dot(v, s.T);
        const sx = Math.max(0, Math.min(im.w - 1, Math.floor((sa + 1) / 2 * im.w))), sy = Math.max(0, Math.min(im.h - 1, Math.floor((1 - sb) / 2 * im.h)));
        const si = (sy * im.w + sx) * 4, di = (y * n + x) * 4;
        out[di] = im.rgba[si]; out[di + 1] = im.rgba[si + 1]; out[di + 2] = im.rgba[si + 2]; out[di + 3] = 255;
      }
      return { w: n, h: n, rgba: out };
    });
  }
  return { SIDES, parseTga, nameOf, toCube };
})();
export default Skybox;
